import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import {
  Mail,
  Newspaper,
  Download,
  ShieldCheck,
  Sparkles,
  Home,
  ChevronRight,
} from "lucide-react";

const SITE = "https://heallexa.com";

export type PressArticleMeta = {
  slug: string;
  title: string;
  outlet: string;
  date: string;
  excerpt: string;
};

export const ARTICLES: PressArticleMeta[] = [
  {
    slug: "end-healthcare-booking-crisis",
    title:
      "How Heallexa is Using WhatsApp and SMS to End the Healthcare Booking Crisis",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa replaces legacy doctor portals with WhatsApp, SMS, and voice AI scheduling across 6.3M records.",
  },
  {
    slug: "disrupt-doctor-directories",
    title:
      "New AI Health Discovery Platform Heallexa Goes Live, Disrupting Traditional Doctor Directories",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa launches a zero-fee discovery network with WhatsApp, SMS, and voice AI booking.",
  },
  {
    slug: "zero-fee-ai-directory-launch",
    title:
      "Heallexa Launches World's Largest AI Healthcare Directory with 6.3 Million Records, Eliminating Per-Appointment Booking Fees for Providers",
    outlet: "Heallexa Newsroom",
    date: "2026-06-08",
    excerpt:
      "Fort Worth, TX, Heallexa debuts a zero-fee provider directory with WhatsApp, SMS, and voice AI booking.",
  },
  {
    slug: "launch-6m-providers",
    title:
      "Heallexa Launches AI Powered Healthcare Search Platform With a Directory of More Than 6.3 Million Provider Profiles",
    outlet: "Heallexa Newsroom",
    date: "2026-05-30",
    excerpt:
      "Dallas, Texas, Heallexa unveils its AI powered healthcare search platform with 6.3M+ provider profiles.",
  },
];

export const Route = createFileRoute("/press")({
  head: () => ({
    meta: [
      { title: "Press Room | Heallexa" },
      {
        name: "description",
        content:
          "Heallexa press room, media coverage, company information, leadership bios, brand assets, and press contact.",
      },
      { property: "og:title", content: "Press Room | Heallexa" },
      {
        property: "og:description",
        content:
          "Media coverage, company facts, and press contact for Heallexa, the AI powered healthcare search platform.",
      },
      { property: "og:url", content: `${SITE}/press` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/press` }],
  }),
  component: PressLayout,
});

export function formatPressDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}, ${y}`;
}

function PressLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isOverview = pathname === "/press" || pathname === "/press/";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Spacer so the fixed white-strip navbar sits on a clean background, not on the banner */}
      <div className="h-14 bg-background" />

      {/* Premium banner */}
      <header className="relative isolate overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10 bg-[#0a1733]" />
        <div
          className="absolute inset-0 -z-10 opacity-90"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 15% 20%, color-mix(in oklab, var(--primary) 70%, transparent) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(56,189,248,0.35) 0%, transparent 50%), linear-gradient(135deg, #0a1733 0%, #0f2557 55%, #0a1733 100%)",
          }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          }}
        />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
        <div className="absolute -top-32 -right-24 -z-10 h-96 w-96 rounded-full bg-sky-400/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 -z-10 h-96 w-96 rounded-full bg-primary/30 blur-3xl" />

        <div className="mx-auto max-w-7xl px-6 pt-14 pb-16 lg:pt-20 lg:pb-20">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/70">
            <span className="inline-block h-px w-8 bg-amber-300/70" />
            <Sparkles className="h-3 w-3 text-amber-300" />
            Heallexa Newsroom
          </div>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white leading-[1.02]">
            Press &amp; Media{" "}
            <span className="block italic font-light bg-gradient-to-r from-amber-200 via-white to-sky-200 bg-clip-text text-transparent">
              Resources
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/80 leading-relaxed">
            Coverage, company information, and assets for journalists writing about Heallexa, the
            AI powered healthcare search platform trusted by patients and providers nationwide.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="mailto:press@heallexa.com">
              <Button size="lg" className="bg-white text-[#0a1733] hover:bg-white/90 shadow-lg shadow-black/20">
                <Mail className="mr-2 h-4 w-4" /> Contact Press Team
              </Button>
            </a>
            <Link to="/press">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white backdrop-blur-sm"
              >
                <Download className="mr-2 h-4 w-4" /> Brand Assets
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-sm">
            {[
              { k: "6.3M+", v: "Provider profiles" },
              { k: "50", v: "U.S. states" },
              { k: "AI", v: "Search experience" },
              { k: "2026", v: "Founded" },
            ].map((s) => (
              <div key={s.v} className="bg-white/[0.04] px-5 py-4">
                <div className="text-2xl font-semibold text-white tracking-tight">{s.k}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-white/60">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Body: sidebar + outlet */}
      <div className="mx-auto max-w-7xl px-6 py-10 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        <aside className="lg:sticky lg:top-20 lg:self-start space-y-5">
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
            <Link
              to="/press"
              className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors ${
                isOverview ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
              }`}
            >
              <Home className="h-4 w-4" />
              Press Room Overview
            </Link>

            <div className="border-t border-border px-4 py-3 flex items-center gap-2">
              <Newspaper className="h-3.5 w-3.5 text-primary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Articles
              </p>
            </div>

            <ul className="divide-y divide-border">
              {ARTICLES.map((a) => {
                const href = `/press/${a.slug}`;
                const active = pathname === href || pathname === `${href}/`;
                return (
                  <li key={a.slug}>
                    <Link
                      to={href}
                      className={`group block w-full text-left px-4 py-3.5 transition-colors ${
                        active ? "bg-primary/5" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                            active ? "text-primary" : "text-muted-foreground"
                          }`}
                        >
                          {a.outlet}
                        </p>
                        <time className="text-[10px] text-muted-foreground tabular-nums">
                          {formatPressDate(a.date)}
                        </time>
                      </div>
                      <p
                        className={`mt-1.5 text-sm font-medium leading-snug line-clamp-3 ${
                          active ? "text-foreground" : "text-foreground/90 group-hover:text-foreground"
                        }`}
                      >
                        {a.title}
                      </p>
                      <div
                        className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${
                          active ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                        }`}
                      >
                        Read article <ChevronRight className="h-3 w-3" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-gradient-to-b from-card to-card/50 p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Quick Facts
              </p>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <Row k="Founded" v="2026" />
              <Row k="Category" v="AI Healthcare Search" />
              <Row k="Headquarters" v="Dallas, TX" />
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Website</dt>
                <dd>
                  <a href={SITE} className="text-primary hover:underline font-medium">
                    heallexa.com
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </aside>

        <main className="min-w-0">
          <div className="rounded-2xl border border-border bg-card shadow-sm p-6 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>

      <FooterSection />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-3 last:border-0 last:pb-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-foreground font-semibold text-right">{v}</dd>
    </div>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary">
        <span className="inline-block h-px w-6 bg-primary/60" />
        {eyebrow}
      </div>
      <h2 className="mt-3 text-3xl md:text-[2rem] font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
